import { t } from 'localization'
import ReactMarkdown from 'react-markdown'
import Image from 'shared/components/Image'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { colors, fonts, media } from 'shared/lib'
import { Store } from 'shared/types/Store'
import { getStoreImageUrl, STORE_DETAILS, StoreID } from 'shared/utils/store-utils'
import styled, { css } from 'styled-components'

const TP = 'shared.stores.components.FlightClubStore'

const DATA_QA_STORE_MAP = {
  [StoreID.NY]: {
    address: 'FcnyStoreAddress',
    map: 'FcnyMapLink',
  },
  [StoreID.LA]: {
    address: 'FclaStoreAddress',
    map: 'FclaMapLink',
  },
  [StoreID.MIA]: {
    address: 'FcmiaStoreAddress',
    map: 'FcmiaMapLink',
  },
  [StoreID.TYO]: {
    address: 'FctyoStoreAddress',
    map: 'FctyoMapLink',
  },
}

const NEW_LINE_RESPONSE = '\r\n'
const NEW_LINE_MARKDOWN = '  \r'

export const FlightClubStorePanel = ({
  address1,
  address2,
  consignmentAddress1,
  consignmentAddress2,
  consignmentHours,
  id,
  mapUrl,
  storeHours,
  title,
}: Store) => {
  const { isMobile } = useWindowSize()

  return (
    <StorePanel>
      <ImageContainer>
        <a
          data-qa={`StoreImage${STORE_DETAILS[id as StoreID]?.name.toUpperCase()}`}
          href={`storelocator/${STORE_DETAILS[id as StoreID]?.url}`}
        >
          <StyledImage src={getStoreImageUrl(id, isMobile)} alt="Flight Club Stores" />
        </a>
      </ImageContainer>
      <StoreDetails>
        <StoreDetailsHeader>
          <StoreName>
            <StoreLink
              data-qa={`StoreTitle${STORE_DETAILS[id as StoreID]?.name.toUpperCase()}`}
              href={`storelocator/${STORE_DETAILS[id as StoreID]?.url}`}
            >
              {title}
            </StoreLink>
          </StoreName>
          <AddressLink
            target="_blank"
            href={mapUrl}
            data-qa={DATA_QA_STORE_MAP[id as StoreID]?.address}
          >
            <StoreAddress>
              {address1}
              <br />
              {address2}
            </StoreAddress>
          </AddressLink>
        </StoreDetailsHeader>
        <StoreHoursTitle>{t(`${TP}.storeHours`, 'Store Hours')}</StoreHoursTitle>
        <StoreHours>
          <ReactMarkdown linkTarget="_blank">
            {storeHours.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
          </ReactMarkdown>
        </StoreHours>
        <StoreHoursTitle>{t(`${TP}.consignment`, 'Consignment')}</StoreHoursTitle>
        {consignmentAddress1 && (
          <ConsignmentAddress>
            {consignmentAddress1}
            {consignmentAddress2 && (
              <>
                <br />
                {consignmentAddress2}
              </>
            )}
          </ConsignmentAddress>
        )}
        <StoreHours>
          <ReactMarkdown linkTarget="_blank">
            {consignmentHours.replace(NEW_LINE_RESPONSE, NEW_LINE_MARKDOWN)}
          </ReactMarkdown>
        </StoreHours>
      </StoreDetails>
    </StorePanel>
  )
}

const StorePanel = styled.div`
  padding-top: 20px;
  color: ${colors.FC2_GREY};
`

const StoreDetails = styled.div`
  overflow: auto;
  line-height: 1.4;
`

const StoreDetailsHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  margin-top: 24px;
  margin-bottom: 12px;
`

const StoreLink = styled.a`
  color: ${colors.FC2_BLACK};
  font-weight: 500;
  font-size: 20px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const StoreName = styled.h2`
  margin-top: 0;
  margin-bottom: 3px;
  max-width: 60%;
  line-height: 140%;
  text-align: left;
  ${media.large`
    line-height: 120%;
  `}
`

const TextLink = css`
  cursor: pointer;
  color: ${colors.FC2_GREY};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const AddressLink = styled.a`
  ${TextLink}
  max-width: 55%;
  text-align: right;
`

const StoreHoursTitle = styled.h3`
  ${fonts.SUBTITLE_2}
  margin: 0;
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.4px;
  text-align: left;
`

const StoreHours = styled.div`
  a {
    ${TextLink}
  }

  p {
    ${fonts.SUBTITLE_3}
    font-size: 14px;
    line-height: 140%;
    font-weight: 400;
    color: ${colors.FC2_DARK_GREY};
    margin-top: 4px;
    margin-bottom: 16px;
  }

  li {
    ${fonts.SUBTITLE_3}
    font-weight: 400;
    margin: 0;
  }
`

const ConsignmentAddress = styled.div`
  ${fonts.SUBTITLE_3}
  color: ${colors.FC2_GREY};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  margin: 4px 0;
`

const StoreAddress = styled.address`
  font-style: normal;
  color: ${colors.FC2_GREY};
  font-size: 14px;
  line-height: 140%;
`

const ImageContainer = styled.div`
  position: relative;
  object-fit: contain;
  max-height: 714px;
  height: 478px;
  ${media.large`
    height: 253px;
  `}
  ${media.extraLarge`
    height: 408px;
  `}
`

const StyledImage = styled(Image)`
  object-fit: cover;
  object-position: 30% 90%;
`
